<template>
    <div class="mb-8 w-full">
        <div
            class="inline-flex items-center gap-1 rounded-[2px] bg-green-500 px-2 py-1 pt-1.5 text-sm leading-0 text-black">
            <span>
                <slot name="text">
                    {{ snippets['headless.pdp.loyaltyUsp'] ?? 'Je spaarbonus voor dit item is ' }}
                </slot>
                <Price :price="calculateLoyaltySaving(listPrice)" />
            </span>
            <span
                class="relative inline-flex shrink-0"
                @mouseenter="onLoyaltyTooltipPointerEnter"
                @mouseleave="onLoyaltyTooltipPointerLeave">
                <button
                    ref="loyaltyTooltipTriggerRef"
                    type="button"
                    class="inline-flex rounded-sm text-black/80 hover:text-black focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-black/50"
                    :aria-describedby="isLoyaltyTooltipOpen ? loyaltyTooltipContentId : undefined"
                    :aria-label="loyaltyTooltipTriggerLabel"
                    @focus="showLoyaltyTooltip"
                    @blur="onLoyaltyTooltipTriggerBlur"
                    @keydown.escape.stop.prevent="hideLoyaltyTooltipImmediate">
                    <InformationCircleIcon class="-mt-0.5 size-4 stroke-2" aria-hidden="true" />
                </button>
                <div
                    v-show="isLoyaltyTooltipOpen"
                    :id="loyaltyTooltipContentId"
                    role="tooltip"
                    v-html="loyaltyTooltipBody"
                    class="pointer-events-auto absolute top-full right-0 z-10 mt-1 w-[min(250px,calc(100vw-1.5rem))] max-w-[200px] rounded-[2px] bg-white p-2 text-left text-xs leading-snug text-black/90" />
            </span>
        </div>
    </div>
</template>

<script lang="ts" setup>
    import { InformationCircleIcon } from '@heroicons/vue/24/outline';

    defineProps<{
        listPrice: number;
    }>();

    const snippetsStore = useSnippetsStore();
    const snippets = snippetsStore.snippets;

    const { calculateLoyaltySaving } = useLoyaltyProgram();

    const loyaltyTooltipContentId = useUniqueId('loyalty-tooltip');
    const isLoyaltyTooltipOpen = ref(false);
    const loyaltyTooltipTriggerRef = ref<HTMLButtonElement | null>(null);
    let loyaltyTooltipCloseTimer: ReturnType<typeof setTimeout> | null = null;

    const loyaltyTooltipTriggerLabel = computed(
        () => snippets['headless.pdp.loyaltyTooltipTriggerLabel'] ?? 'Meer informatie over het spaarbedrag voor leden',
    );
    const loyaltyTooltipBody = computed(
        () =>
            snippets['headless.pdp.loyaltyTooltipBody'] ??
            'Als lid spaar je punten op je aankoop. Dit bedrag is een indicatie van het voordeel op basis van het getoonde prijskaartje.',
    );

    function clearLoyaltyTooltipCloseTimer() {
        if (loyaltyTooltipCloseTimer !== null) {
            clearTimeout(loyaltyTooltipCloseTimer);
            loyaltyTooltipCloseTimer = null;
        }
    }

    function showLoyaltyTooltip() {
        clearLoyaltyTooltipCloseTimer();
        isLoyaltyTooltipOpen.value = true;
    }

    function scheduleHideLoyaltyTooltip() {
        clearLoyaltyTooltipCloseTimer();
        loyaltyTooltipCloseTimer = setTimeout(() => {
            const trigger = loyaltyTooltipTriggerRef.value;
            if (trigger && document.activeElement === trigger) {
                return;
            }
            isLoyaltyTooltipOpen.value = false;
            loyaltyTooltipCloseTimer = null;
        }, 150);
    }

    function hideLoyaltyTooltipImmediate() {
        clearLoyaltyTooltipCloseTimer();
        isLoyaltyTooltipOpen.value = false;
    }

    function onLoyaltyTooltipTriggerBlur() {
        requestAnimationFrame(() => {
            const trigger = loyaltyTooltipTriggerRef.value;
            if (trigger && document.activeElement === trigger) {
                return;
            }
            isLoyaltyTooltipOpen.value = false;
        });
    }

    function onLoyaltyTooltipPointerEnter() {
        showLoyaltyTooltip();
    }

    function onLoyaltyTooltipPointerLeave() {
        scheduleHideLoyaltyTooltip();
    }

    onBeforeUnmount(() => {
        clearLoyaltyTooltipCloseTimer();
    });
</script>
